import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import React, { FC, useEffect, useRef, useState } from 'react';

import { MOBILE_WIDTH } from '../../constants';

import {
  ToolTipButton,
  InfoIcon,
  InfoGrayIcon,
  ToolTipContainer,
  Caret,
  CaretMask,
  ToolTipBubble,
  ExitIcon,
  ToolTipHeading,
  ToolTipText,
  ModalMask,
} from './styles';

interface ToolTipProps {
  id: string;
  heading: string;
  body: string;
  isGrayInfoIcon?: boolean;
}

export const ToolTip: FC<ToolTipProps> = ({
  id,
  heading,
  body,
  isGrayInfoIcon = false,
}) => {
  const [showToolTip, setShowToolTip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const toolTipRef = useRef<HTMLButtonElement | null>(null);
  const toolTipContainerRef = useRef<HTMLDivElement | null>(null);

  // Function to close tooltip/modal and restore scrolling
  const closeToolTip = () => {
    setShowToolTip(false);
    setShowModal(false);
    enableBodyScroll(document.body); // Restore scrolling when closing
    toolTipRef.current?.focus(); // Restore focus to the button
  };

  // Handle opening the modal
  const openToolTip = () => {
    setShowToolTip(true);
    if (width < MOBILE_WIDTH) {
      setShowModal(true);
    }
  };

  // Handle keyboard interactions
  const handleBlur = (
    e: React.KeyboardEvent | KeyboardEvent,
    target?: 'button' | 'exit'
  ) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeToolTip();
      enableBodyScroll(document.body);
    }

    if ((e.key === 'Enter' || e.key === ' ') && target === 'exit') {
      e.preventDefault();
      closeToolTip();
      enableBodyScroll(document.body);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        toolTipContainerRef.current &&
        !toolTipContainerRef.current.contains(e.target as Node) &&
        toolTipRef.current !== e.target
      ) {
        closeToolTip();
      }
    };

    if (showToolTip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolTip]);

  // Close tooltip on global Escape key
  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => handleBlur(e);
    if (showToolTip) {
      document.addEventListener('keydown', keyListener);
    }
    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, [showToolTip]);

  // Handle screen resizing
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (showModal && width < MOBILE_WIDTH) {
      disableBodyScroll(document.body);
    }
    return () => {
      clearAllBodyScrollLocks(); // Cleanup scroll locks
    };
  }, [showModal]);

  return (
    <>
      {/* Tooltip Trigger Button */}
      <ToolTipButton
        id={`${id}-button`}
        data-testid={`${id}-button`}
        aria-describedby={`${id}-body`}
        type="button"
        onClick={openToolTip}
        onKeyDown={(e) => handleBlur(e, 'button')}
        ref={toolTipRef}
      >
        {!isGrayInfoIcon ? (
          <InfoIcon
            id={`${id}-button-icon`}
            data-testid={`${id}-button-icon`}
            aria-hidden
          />
        ) : (
          <InfoGrayIcon
            id={`${id}-button-icon`}
            data-testid={`${id}-button-icon`}
            aria-hidden
          />
        )}
        {showToolTip && (
          <ToolTipContainer
            id={id}
            showToolTip={showToolTip}
            showModal={width < MOBILE_WIDTH}
            aria-modal
            ref={toolTipContainerRef}
            tabIndex={-1} // Allow focus trapping
          >
            <Caret />
            <CaretMask />
            <ToolTipBubble>
              {/* Close Button (Exit Icon) */}
              <ExitIcon
                id={`${id}-exit-button`}
                data-testid={`${id}-exit-button`}
                onClick={(e) => {
                  e.stopPropagation(); // Ensure event bubbling doesn’t block it
                  closeToolTip();
                }}
                onKeyDown={(e) => handleBlur(e, 'exit')}
                tabIndex={0} // Ensure it's focusable
              />
              {heading && (
                <ToolTipHeading
                  id={`${id}-heading`}
                  data-testid={`${id}-heading`}
                >
                  {heading}
                </ToolTipHeading>
              )}
              <ToolTipText
                id={`${id}-body`}
                data-testid={`${id}-body`}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            </ToolTipBubble>
          </ToolTipContainer>
        )}
      </ToolTipButton>

      {/* Clicking this should close the modal on mobile */}
      {showToolTip && width <= MOBILE_WIDTH && (
        <ModalMask onClick={closeToolTip} />
      )}
    </>
  );
};
